import { useCreateAuctionMutation } from "@/mutations/auction.ts";
import { defaultMutationErrorHandler } from "@/utils/api.ts";
import { DateTimeFormatter } from "@js-joda/core";
import type { Breakpoint } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import {
    type FormEventHandler,
    type ReactNode,
    useCallback,
    useMemo,
    useRef,
    useState,
} from "react";
import type { DefaultValues } from "react-hook-form";
import { match } from "ts-pattern";
import AuctionDialogRoot from "./-components/AuctionDialogRoot.tsx";
import AuctionForm, {
    type AuctionFieldValues,
    type AuctionTransformedValues,
} from "./-components/AuctionForm.tsx";
import AuctionInitForm, {
    type AuctionInitTransformedValues,
} from "./-components/AuctionInitForm.tsx";

const Root = (): ReactNode => {
    const onSubmitRef = useRef<FormEventHandler>(undefined);
    const [init, setInit] = useState<AuctionInitTransformedValues | null>(null);
    const [open, setOpen] = useState(true);
    const createMutation = useCreateAuctionMutation();

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const defaultValues = useMemo((): DefaultValues<AuctionFieldValues> => {
        if (!init) {
            return {};
        }

        const config: DefaultValues<AuctionFieldValues["config"]> = match(init.type)
            .with("single_parcel", (type) => ({
                type,
            }))
            .otherwise((type) => ({
                type,
                parcels: Array.from({ length: init.numParcels ?? 1 }, (_, index) => ({
                    id: `parcel-${index}`,
                    acreage: "",
                    startingBid: "",
                    biddingIncrement: "",
                })),
                parcelCombinations: [],
            }));

        return {
            sealedBid: init.sealedBid,
            pricePerAcre: init.type === "choice",
            config,
        };
    }, [init]);

    const handleSubmit = useCallback(
        (values: AuctionTransformedValues) => {
            if (!init) {
                return;
            }

            createMutation.mutate(
                {
                    ...values,
                    startsAt: values.startsAt.format(DateTimeFormatter.ISO_OFFSET_DATE_TIME),
                },
                {
                    onSuccess: () => {
                        enqueueSnackbar("Auction has been created", { variant: "success" });
                        setOpen(false);
                    },
                    onError: defaultMutationErrorHandler,
                },
            );
        },
        [init, createMutation],
    );

    let content: ReactNode;
    let maxWidth: Breakpoint = "md";

    if (!init) {
        maxWidth = "xs";
        content = (
            <AuctionInitForm onClose={handleClose} onSubmit={setInit} onSubmitRef={onSubmitRef} />
        );
    } else {
        content = (
            <AuctionForm
                title="Create new Auction"
                type={init.type}
                sealedBid={init.sealedBid}
                defaultValues={defaultValues}
                onClose={handleClose}
                onSubmit={handleSubmit}
                onSubmitRef={onSubmitRef}
            />
        );
    }

    return (
        <AuctionDialogRoot
            open={open}
            onClose={handleClose}
            maxWidth={maxWidth}
            onSubmitRef={onSubmitRef}
        >
            {content}
        </AuctionDialogRoot>
    );
};

export const Route = createFileRoute("/manage/auctions/create")({
    component: Root,
});
